/**
 * Every table this add-in writes into a workbook, by the name Excel stores it under.
 *
 * These are identities, not copy: the name is written once and read back later by `getTableData`,
 * the clear routines and the visibility rules. A name that drifts does not error — the sheet stays
 * in the workbook but becomes invisible to refresh and delete, and any customer formula pointing at
 * it resolves to `#REF`. Renaming one is a breaking change for existing workbooks.
 *
 * `WorkbookTable` is what makes that checkable: `loadDataIntoSheet` and `getTableData` take the union
 * rather than `string`, so a typo is a compile error instead of a silent miss.
 */

/**
 * Raw data tables — hidden `__X` sheets the user can reveal, offered under Reports. Grouped by the
 * datasource that writes them, because that is also the unit they are cleared in.
 */
export const ESTIMATE_DATA_TABLES = [
  "Estimate",
  "AlternateItems",
  "AssemblyItems",
  "SortFields",
  "FloorAreas",
  "Alternates",
  "UseGroups",
  "Attributes",
  "LaborCrews",
] as const;
export const GCGR_DATA_TABLES = ["GCGRStaffing", "GCGRLineItems", "GRLabor"] as const;
export const CONTRACTING_DATA_TABLES = ["BidTabs"] as const;

export const DATA_TABLES = [...ESTIMATE_DATA_TABLES, ...GCGR_DATA_TABLES, ...CONTRACTING_DATA_TABLES] as const;

/** `.metadata` rows recording what the workbook currently holds, plus the two legacy tables. */
export const REPORT_TABLES = [
  "ProjectReport",
  "EstimateReport",
  "GCGRReport",
  "ContractingReport",
  "CostModelReport",
  "Report",
  "GCGR",
] as const;

/** Reference data, written to visible `admin_X` sheets from User Settings. */
export const ADMIN_TABLES = ["UPC", "UPCLaborCrews", "Regions", "MasterFormat", "Uniformat", "Users"] as const;

export type DataTable = (typeof DATA_TABLES)[number];
export type ReportTable = (typeof REPORT_TABLES)[number];
export type AdminTable = (typeof ADMIN_TABLES)[number];
/**
 * Pre-migration names. A workbook built before the `__`-prefix scheme keeps its estimate in a table
 * called `estimateData` on a sheet called `data`; `legacyCheck` remaps writes onto those when it
 * finds them. Nothing creates them any more — they exist only to keep old workbooks working.
 */
export const LEGACY_TABLES = ["estimateData"] as const;
export const LEGACY_ESTIMATE_SHEET = "data";

export type LegacyTable = (typeof LEGACY_TABLES)[number];
export type WorkbookTable = DataTable | ReportTable | AdminTable | LegacyTable;

/** Sheets that hold the `.metadata` rows and the legacy tables — always hidden, never user-toggled. */
export const REPORT_SHEET_NAMES: ReadonlySet<string> = new Set(["__Report", "__GCGR", ".metadata"]);

/**
 * The blank sheet a multi-sheet report is built on before its pages are printed from it.
 *
 * Built in one place and looked up in another — `setWorksheetOption` resolves the same name to store
 * a template's own options — so the two have to agree on the prefix.
 */
export const templateSheetName = (reportTitle: string): string => `Template_${reportTitle}`;

/** The sheets whose presence means "this workbook has data tables loaded". */
export const DATA_SHEET_NAMES: ReadonlySet<string> = new Set(["__Estimate", "__GCGRLineItems", "__BidTabs"]);
